import React, { FC, useCallback, useContext, useMemo, useRef } from 'react';
import { SheetField } from '../../../../../types';
import { useFormContext } from 'react-hook-form';
import { StepContext } from '../../../../../../creator/step.context';
import { getCurrentField } from '../../../../../steps/helpers';
import cn from 'classnames';
import styles from './styles.module.scss';
import { Button, Icon } from '@sravni/react-design-system';
import { ArrowLeft, ArrowRight } from '@sravni/react-icons';
import { FieldsCounter } from './fields.counter';

type SheetFooterProps = React.ComponentPropsWithoutRef<'div'> & {
    customFooter?: JSX.Element;
    onNext?: () => void;
    nextBtnDisabled?: boolean;
    nextBtnText?: string;
    prevBtnDisabled?: boolean;
    onPrev?: () => void;
    fieldsList: SheetField[];
    hideFieldCounter?: boolean;
};

export const SheetFooter: FC<SheetFooterProps> = (props) => {
    const {
        className,
        customFooter: CustomFooter,
        nextBtnDisabled,
        nextBtnText,
        prevBtnDisabled,
        onPrev,
        onNext,
        fieldsList,
        hideFieldCounter,
        ...other
    } = props;
    const form = useFormContext();
    const { focusedField, setFocusedField, nextStep } = useContext(StepContext);

    const {
        trigger,
        formState: { dirtyFields: _, errors },
    } = form;

    const { field: currentField, index: currentFieldIndex } = useMemo(
        () => getCurrentField(fieldsList, focusedField),
        [fieldsList, focusedField],
    );

    const prevFocusedField: React.MouseEventHandler<HTMLButtonElement> = useCallback(
        (event) => {
            event.preventDefault();
            if (currentFieldIndex > 0) {
                setFocusedField(fieldsList[currentFieldIndex - 1].name);
            }
        },
        [currentFieldIndex, setFocusedField, fieldsList],
    );

    const stringifiedErrors = Object.keys(errors).join(' ');

    const nextFocusedField: React.MouseEventHandler<HTMLButtonElement> = useCallback(
        async (event) => {
            event.preventDefault();
            if (currentFieldIndex < fieldsList.length - 1) {
                const isValid = await trigger(focusedField || '');
                if (isValid) {
                    await trigger(Object.keys(errors));
                    setFocusedField(fieldsList[currentFieldIndex + 1].name);
                }
            } else {
                const isValid = await trigger();
                if (isValid) {
                    nextStep();
                }
            }
        },
        [currentFieldIndex, setFocusedField, nextStep, trigger, fieldsList, focusedField, stringifiedErrors],
    );

    const fieldHasErrors = useMemo(
        () => Boolean(currentField?.name && !!errors[currentField?.name]),
        [stringifiedErrors, currentField],
    );
    const anyFieldHasError = useMemo(() => {
        if (currentFieldIndex !== fieldsList.length - 1) {
            return false;
        }
        return Object.keys(errors).length > 0;
    }, [stringifiedErrors, currentFieldIndex, fieldsList]);

    const isPrevDisabled = currentFieldIndex <= 0;

    return (
        <div className={cn(styles['sheet-footer'], className)} {...other}>
            {CustomFooter ? (
                <>{CustomFooter}</>
            ) : (
                <div className={styles['sheet-footer__content']}>
                    <div className={styles['prev-btn-container']}>
                        {!isPrevDisabled ? (
                            <Button color="gray" variant="secondary" type="button" onClick={prevFocusedField} size={44}>
                                <Icon size={20} icon={<ArrowLeft />} strong />
                            </Button>
                        ) : (
                            <div />
                        )}
                        <FieldsCounter
                            isHidden={hideFieldCounter}
                            currentField={currentFieldIndex + 1}
                            fields={fieldsList.length}
                        />
                    </div>

                    <Button
                        color="gray"
                        variant="primary"
                        type="button"
                        onClick={nextFocusedField}
                        disabled={nextBtnDisabled || fieldHasErrors || anyFieldHasError}
                        size={44}>
                        {nextBtnText || 'Далее'}
                        <Icon size={20} strong icon={<ArrowRight />} />
                    </Button>
                </div>
            )}
        </div>
    );
};
